import { useMemo, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BankAccount, Batch, Instruction } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

interface Exceptions {
  unmatchedStatementLines: Array<{ id: string; ref: string; amount: string }>;
  failedInstructions: Instruction[];
}

export function Reconciliation() {
  const { persona } = useAuth();
  const [showImport, setShowImport] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [result, setResult] = useState<{ matched: number; exceptions: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const accountsQ = useApiQuery<{ items: BankAccount[] }>('/api/v1/payments/accounts');
  const exceptionsQ = useApiQuery<Exceptions>('/api/v1/payments/exceptions');
  const canCash = persona?.roles.includes('MOF_CASH_MANAGER') ?? false;

  const runReconcile = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ matched: number; exceptions: number }>('/api/v1/payments/reconcile/run');
      setResult(r);
      exceptionsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Reconciliation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Bank reconciliation"
        subtitle="Import the CBS daily statement (camt.053) and auto-match settlement lines to transmitted instructions"
        actions={
          canCash ? (
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setShowAccount(true)}>
                + Bank account
              </button>
              <button className="btn-secondary" onClick={() => setShowImport(true)}>
                Import CBS statement
              </button>
              <button className="btn-primary" onClick={runReconcile} disabled={busy}>
                {busy ? 'Reconciling…' : 'Run reconciliation'}
              </button>
            </div>
          ) : undefined
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {result ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">
          Reconciliation run complete — {result.matched} instruction(s) settled, {result.exceptions} exception(s).
          Settled vouchers are marked PAID back at the ministry.
        </div>
      ) : null}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Treasury accounts at CBS</h2>
        <div className="card overflow-x-auto">
          {accountsQ.loading ? (
            <Spinner />
          ) : (accountsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No accounts registered" hint="Register the TSA main account first." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Account</th>
                  <th className="table-head">Holder</th>
                  <th className="table-head">CBS reference</th>
                  <th className="table-head">Currency</th>
                </tr>
              </thead>
              <tbody>
                {accountsQ.data!.items.map((a) => (
                  <tr key={a.id}>
                    <td className="table-cell font-medium">{a.name}</td>
                    <td className="table-cell">{a.holder}</td>
                    <td className="table-cell tabular-nums">{a.cbsRef}</td>
                    <td className="table-cell">{a.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <h2 className="mb-2 text-sm font-bold text-slate-700">Unmatched statement lines</h2>
          <div className="card overflow-x-auto">
            {(exceptionsQ.data?.unmatchedStatementLines.length ?? 0) === 0 ? (
              <EmptyState title="None — statement fully matched" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Reference</th>
                    <th className="table-head text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptionsQ.data!.unmatchedStatementLines.map((l) => (
                    <tr key={l.id}>
                      <td className="table-cell text-xs">{l.ref}</td>
                      <td className="table-cell text-right tabular-nums">{scr(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div>
          <h2 className="mb-2 text-sm font-bold text-slate-700">Failed instructions</h2>
          <div className="card overflow-x-auto">
            {(exceptionsQ.data?.failedInstructions.length ?? 0) === 0 ? (
              <EmptyState title="No failed payments" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">End-to-end ref</th>
                    <th className="table-head">Beneficiary</th>
                    <th className="table-head text-right">Amount</th>
                    <th className="table-head">Return</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptionsQ.data!.failedInstructions.map((i) => (
                    <tr key={i.id}>
                      <td className="table-cell text-xs">{i.endToEndRef}</td>
                      <td className="table-cell">{i.beneficiaryName}</td>
                      <td className="table-cell text-right tabular-nums">{scr(i.amount)}</td>
                      <td className="table-cell text-xs text-rose-600">
                        {i.returnCode} — {i.returnReason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showImport ? (
        <ImportModal
          accounts={accountsQ.data?.items ?? []}
          onClose={() => setShowImport(false)}
          onDone={() => {
            setShowImport(false);
            exceptionsQ.reload();
          }}
        />
      ) : null}
      {showAccount ? (
        <AccountModal
          onClose={() => setShowAccount(false)}
          onDone={() => {
            setShowAccount(false);
            accountsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ImportModal({ accounts, onClose, onDone }: { accounts: BankAccount[]; onClose: () => void; onDone: () => void }) {
  const batchesQ = useApiQuery<{ items: Batch[] }>('/api/v1/payments/batches?status=TRANSMITTED&limit=100');
  const [accountId, setAccountId] = useState(accounts.find((a) => a.holder === 'TSA')?.id ?? '');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const transmitted = useMemo(
    () =>
      (batchesQ.data?.items ?? []).flatMap((b) => (b.instructions ?? []).filter((i) => i.status === 'TRANSMITTED')),
    [batchesQ.data],
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const lines = transmitted
        .filter((i) => selected.has(i.id))
        .map((i) => ({ ref: i.endToEndRef, amount: Number.parseFloat(i.amount) }));
      await api.post('/api/v1/payments/statements/import', {
        accountId,
        date,
        fileRef: `camt053-${date}-${lines.length}`,
        lines,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Import CBS daily statement" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">
          In production this is the camt.053 file received from the Central Bank. For the dev drop, tick the
          transmitted instructions that appear on today's statement.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Account">
            <select className="field-input" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Select account…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Statement date">
            <input className="field-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
          {batchesQ.loading ? (
            <Spinner />
          ) : transmitted.length === 0 ? (
            <EmptyState title="No transmitted instructions awaiting settlement" />
          ) : (
            transmitted.map((i) => (
              <label key={i.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selected.has(i.id)}
                  onChange={() => {
                    const next = new Set(selected);
                    if (next.has(i.id)) next.delete(i.id);
                    else next.add(i.id);
                    setSelected(next);
                  }}
                />
                <span className="flex-1 text-sm">
                  {i.endToEndRef} · {i.beneficiaryName}
                </span>
                <span className="text-sm tabular-nums">{scr(i.amount)}</span>
              </label>
            ))
          )}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !accountId || selected.size === 0}>
            {busy ? 'Importing…' : `Import ${selected.size} line(s)`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AccountModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ holder: 'SUBACCOUNT' as 'TSA' | 'SUBACCOUNT', name: '', cbsRef: '', currency: 'SCR' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/payments/accounts', form);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register treasury account at CBS" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Holder type">
          <select
            className="field-input"
            value={form.holder}
            onChange={(e) => setForm({ ...form, holder: e.target.value as 'TSA' | 'SUBACCOUNT' })}
          >
            <option value="TSA">TSA — Treasury Single Account</option>
            <option value="SUBACCOUNT">Sub-account</option>
          </select>
        </Field>
        <Field label="Account name">
          <input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CBS reference">
            <input className="field-input" value={form.cbsRef} onChange={(e) => setForm({ ...form, cbsRef: e.target.value })} />
          </Field>
          <Field label="Currency">
            <input className="field-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.name || !form.cbsRef}>
            {busy ? 'Registering…' : 'Register account'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
